import type { ModelDefinition } from "@/models.js";

/**
 * Preset system voices for the Qwen-Audio-3.0-TTS models, passed through 1:1
 * as the upstream DashScope `voice` parameter. Each model only supports its
 * own voice set (voices cannot be mixed between models), and the first entry
 * is used as the default when the caller omits `voice`. The gateway validates
 * `voice` against this allowlist, so only these system voices are accepted;
 * Alibaba's cloned/custom voice ids are not supported through the speech
 * endpoint today.
 * Ref: https://help.aliyun.com/zh/model-studio/qwen-audio-tts-voice-list
 */
const QWEN_AUDIO_TTS_PLUS_VOICES = ["longanlingxin", "longanlufeng"];
const QWEN_AUDIO_TTS_FLASH_VOICES = [
	"longanhuan_v3.6",
	"longjielidou_v3.6",
	"loongeva_v3.6",
	"loongjohn",
];

export const alibabaModels = [
	{
		id: "qwen-max",
		name: "Qwen Max",
		description: "Alibaba's flagship Qwen model with vision and tool support.",
		family: "alibaba",
		releasedAt: new Date("2025-09-05"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-max",
				inputPrice: "1.6e-6",
				outputPrice: "6.4e-6",
				regions: [
					{ id: "singapore" },
					{
						id: "cn-beijing",
						inputPrice: "0.345e-6",
						outputPrice: "1.377e-6",
					},
				],
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 8192,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen-max-latest",
		name: "Qwen Max Latest",
		description: "Latest Qwen Max with enhanced capabilities.",
		family: "alibaba",
		releasedAt: new Date("2025-01-25"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-max-latest",
				inputPrice: "1.6e-6",
				outputPrice: "6.4e-6",
				regions: [
					{ id: "singapore" },
					{
						id: "cn-beijing",
						inputPrice: "0.345e-6",
						outputPrice: "1.377e-6",
						contextSize: 131072,
					},
				],
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 8192,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				// Both declared regions are dead: Singapore answers 403 access_denied
				// and Beijing 404 model_not_found, while plain `qwen-max` still serves
				// in both.
				deactivatedAt: new Date("2026-08-11"),
			},
		],
	},
	{
		id: "qwen-plus",
		name: "Qwen Plus",
		description: "Mid-tier Qwen model balancing speed and capability.",
		family: "alibaba",
		releasedAt: new Date("2025-01-25"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen-plus",
				inputPrice: "0.4e-6",
				outputPrice: "1.2e-6",
				cachedInputPrice: "0.08e-6",
				cacheReadInputPrice: "0.04e-6",
				cacheWriteInputPrice: "0.5e-6",
				pricingTiers: [
					{
						name: "Up to 256K",
						upToTokens: 256000,
						inputPrice: "0.4e-6",
						outputPrice: "1.2e-6",
						cachedInputPrice: "0.08e-6",
						cacheReadInputPrice: "0.04e-6",
						cacheWriteInputPrice: "0.5e-6",
					},
					{
						name: "Over 256K",
						upToTokens: Infinity,
						inputPrice: "1.2e-6",
						outputPrice: "3.6e-6",
						cachedInputPrice: "0.24e-6",
						cacheReadInputPrice: "0.12e-6",
						cacheWriteInputPrice: "1.5e-6",
					},
				],
				regions: [
					{ id: "singapore" },
					{ id: "eu-frankfurt" },
					{
						id: "us-virginia",
						inputPrice: "0.115e-6",
						outputPrice: "0.287e-6",
						cachedInputPrice: "0.023e-6",
						cacheReadInputPrice: "0.0115e-6",
						cacheWriteInputPrice: "0.14375e-6",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.115e-6",
								outputPrice: "0.287e-6",
								cachedInputPrice: "0.023e-6",
								cacheReadInputPrice: "0.0115e-6",
								cacheWriteInputPrice: "0.14375e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "0.345e-6",
								outputPrice: "0.861e-6",
								cachedInputPrice: "0.069e-6",
								cacheReadInputPrice: "0.0345e-6",
								cacheWriteInputPrice: "0.43125e-6",
							},
						],
					},
					{
						id: "cn-beijing",
						inputPrice: "0.115e-6",
						outputPrice: "0.287e-6",
						cachedInputPrice: "0.023e-6",
						cacheReadInputPrice: "0.0115e-6",
						cacheWriteInputPrice: "0.14375e-6",
						pricingTiers: [
							{
								name: "Up to 128K",
								upToTokens: 128000,
								inputPrice: "0.115e-6",
								outputPrice: "0.287e-6",
								cachedInputPrice: "0.023e-6",
								cacheReadInputPrice: "0.0115e-6",
								cacheWriteInputPrice: "0.14375e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.345e-6",
								outputPrice: "2.868e-6",
								cachedInputPrice: "0.069e-6",
								cacheReadInputPrice: "0.0345e-6",
								cacheWriteInputPrice: "0.43125e-6",
							},
							{
								name: "256K-1M",
								upToTokens: 1000000,
								inputPrice: "0.689e-6",
								outputPrice: "6.881e-6",
								cachedInputPrice: "0.138e-6",
								cacheReadInputPrice: "0.0689e-6",
								cacheWriteInputPrice: "0.86125e-6",
							},
						],
					},
				],
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen-plus-latest",
		name: "Qwen Plus Latest",
		description: "Updated Qwen Plus with extended context support.",
		family: "alibaba",
		releasedAt: new Date("2024-09-09"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen-plus-latest",
				inputPrice: "0.4e-6",
				outputPrice: "1.2e-6",
				cachedInputPrice: "0.08e-6",
				cacheReadInputPrice: "0.04e-6",
				cacheWriteInputPrice: "0.5e-6",
				pricingTiers: [
					{
						name: "Up to 256K",
						upToTokens: 256000,
						inputPrice: "0.4e-6",
						outputPrice: "1.2e-6",
						cachedInputPrice: "0.08e-6",
						cacheReadInputPrice: "0.04e-6",
						cacheWriteInputPrice: "0.5e-6",
					},
					{
						name: "Over 256K",
						upToTokens: Infinity,
						inputPrice: "1.2e-6",
						outputPrice: "3.6e-6",
						cachedInputPrice: "0.24e-6",
						cacheReadInputPrice: "0.12e-6",
						cacheWriteInputPrice: "1.5e-6",
					},
				],
				regions: [
					{ id: "singapore" },
					{
						id: "cn-beijing",
						inputPrice: "0.115e-6",
						outputPrice: "0.287e-6",
						cachedInputPrice: "0.023e-6",
						cacheReadInputPrice: "0.0115e-6",
						cacheWriteInputPrice: "0.14375e-6",
						pricingTiers: [
							{
								name: "Up to 128K",
								upToTokens: 128000,
								inputPrice: "0.115e-6",
								outputPrice: "0.287e-6",
								cachedInputPrice: "0.023e-6",
								cacheReadInputPrice: "0.0115e-6",
								cacheWriteInputPrice: "0.14375e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.345e-6",
								outputPrice: "2.868e-6",
								cachedInputPrice: "0.069e-6",
								cacheReadInputPrice: "0.0345e-6",
								cacheWriteInputPrice: "0.43125e-6",
							},
							{
								name: "256K-1M",
								upToTokens: 1000000,
								inputPrice: "0.689e-6",
								outputPrice: "6.881e-6",
								cachedInputPrice: "0.138e-6",
								cacheReadInputPrice: "0.0689e-6",
								cacheWriteInputPrice: "0.86125e-6",
							},
						],
					},
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 32000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen-flash",
		name: "Qwen Flash",
		description: "Fast, cost-effective Qwen for high-volume tasks.",
		family: "alibaba",
		releasedAt: new Date("2024-09-09"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen-flash",
				inputPrice: "0.05e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.01e-6",
				cacheReadInputPrice: "0.005e-6",
				cacheWriteInputPrice: "0.0625e-6",
				regions: [
					{
						id: "singapore",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.05e-6",
								outputPrice: "0.4e-6",
								cachedInputPrice: "0.01e-6",
								cacheReadInputPrice: "0.005e-6",
								cacheWriteInputPrice: "0.0625e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "0.25e-6",
								outputPrice: "2.0e-6",
								cachedInputPrice: "0.05e-6",
								cacheReadInputPrice: "0.025e-6",
								cacheWriteInputPrice: "0.3125e-6",
							},
						],
					},
					{
						id: "eu-frankfurt",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.05e-6",
								outputPrice: "0.4e-6",
								cachedInputPrice: "0.01e-6",
								cacheReadInputPrice: "0.005e-6",
								cacheWriteInputPrice: "0.0625e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "0.25e-6",
								outputPrice: "2.0e-6",
								cachedInputPrice: "0.05e-6",
								cacheReadInputPrice: "0.025e-6",
								cacheWriteInputPrice: "0.3125e-6",
							},
						],
					},
					{
						id: "us-virginia",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.05e-6",
								outputPrice: "0.4e-6",
								cachedInputPrice: "0.01e-6",
								cacheReadInputPrice: "0.005e-6",
								cacheWriteInputPrice: "0.0625e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "0.25e-6",
								outputPrice: "2.0e-6",
								cachedInputPrice: "0.05e-6",
								cacheReadInputPrice: "0.025e-6",
								cacheWriteInputPrice: "0.3125e-6",
							},
						],
					},
					{
						id: "cn-beijing",
						inputPrice: "0.022e-6",
						outputPrice: "0.216e-6",
						cachedInputPrice: "0.0044e-6",
						cacheReadInputPrice: "0.0022e-6",
						cacheWriteInputPrice: "0.0275e-6",
						pricingTiers: [
							{
								name: "Up to 128K",
								upToTokens: 128000,
								inputPrice: "0.022e-6",
								outputPrice: "0.216e-6",
								cachedInputPrice: "0.0044e-6",
								cacheReadInputPrice: "0.0022e-6",
								cacheWriteInputPrice: "0.0275e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.087e-6",
								outputPrice: "0.861e-6",
								cachedInputPrice: "0.017e-6",
								cacheReadInputPrice: "0.0087e-6",
								cacheWriteInputPrice: "0.10875e-6",
							},
							{
								name: "256K-1M",
								upToTokens: 1000000,
								inputPrice: "0.173e-6",
								outputPrice: "1.721e-6",
								cachedInputPrice: "0.035e-6",
								cacheReadInputPrice: "0.0173e-6",
								cacheWriteInputPrice: "0.21625e-6",
							},
						],
					},
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 32000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen-omni-turbo",
		name: "Qwen Omni Turbo",
		description: "Multimodal Qwen with vision support.",
		family: "alibaba",
		releasedAt: new Date("2025-03-26"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen-omni-turbo",
				inputPrice: "0.2e-6",
				outputPrice: "0.8e-6",
				regions: [{ id: "singapore" }, { id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 8192,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				deactivatedAt: new Date("2026-10-10"),
			},
		],
	},
	{
		id: "qwen-turbo",
		name: "Qwen Turbo",
		description: "Fastest Qwen model for quick responses.",
		family: "alibaba",
		releasedAt: new Date("2025-02-01"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen-turbo",
				inputPrice: "0.05e-6",
				outputPrice: "0.2e-6",
				regions: [
					{ id: "singapore" },
					{
						id: "cn-beijing",
						inputPrice: "0.044e-6",
						outputPrice: "0.087e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-13"),
			},
		],
	},
	{
		id: "qwen3-coder-plus",
		name: "Qwen3 Coder Plus",
		description: "Advanced Qwen for code generation.",
		family: "alibaba",
		releasedAt: new Date("2025-09-23"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen3-coder-plus",
				inputPrice: "1e-6",
				outputPrice: "5e-6",
				cachedInputPrice: "0.2e-6",
				cacheReadInputPrice: "0.1e-6",
				cacheWriteInputPrice: "1.25e-6",
				pricingTiers: [
					{
						name: "Up to 32K",
						upToTokens: 32000,
						inputPrice: "1e-6",
						outputPrice: "5e-6",
						cachedInputPrice: "0.2e-6",
						cacheReadInputPrice: "0.1e-6",
						cacheWriteInputPrice: "1.25e-6",
					},
					{
						name: "32K-128K",
						upToTokens: 128000,
						inputPrice: "1.8e-6",
						outputPrice: "9e-6",
						cachedInputPrice: "0.36e-6",
						cacheReadInputPrice: "0.18e-6",
						cacheWriteInputPrice: "2.25e-6",
					},
					{
						name: "128K-256K",
						upToTokens: 256000,
						inputPrice: "3e-6",
						outputPrice: "15e-6",
						cachedInputPrice: "0.6e-6",
						cacheReadInputPrice: "0.3e-6",
						cacheWriteInputPrice: "3.75e-6",
					},
					{
						name: "256K-1M",
						upToTokens: 1000000,
						inputPrice: "6e-6",
						outputPrice: "60e-6",
						cachedInputPrice: "1.2e-6",
						cacheReadInputPrice: "0.6e-6",
						cacheWriteInputPrice: "7.5e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 66000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-10-10"),
			},
		],
	},
	{
		id: "qwen-qwq-32b",
		name: "Qwen QwQ 32B",
		description: "Qwen 32B reasoning model.",
		family: "alibaba",
		releasedAt: new Date("2025-03-01"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/QwQ-32B",
				inputPrice: "0.15e-6",
				outputPrice: "0.45e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: true,
				deactivatedAt: new Date("2025-11-03"),
			},
		],
	},
	{
		id: "qwen3-235b-a22b-instruct-2507",
		name: "Qwen3 235B A22B Instruct 2507",
		description: "Large Qwen 3 MoE instruction model.",
		family: "alibaba",
		releasedAt: new Date("2025-07-21"),
		providers: [
			{
				providerId: "nebius",
				stability: "unstable",
				externalId: "Qwen/Qwen3-235B-A22B-Instruct-2507",
				deactivatedAt: new Date("2026-08-31"),
				inputPrice: "0.2e-6",
				outputPrice: "0.6e-6",
				requestPrice: "0",
				contextSize: 262000,
				maxOutput: 8192,
				quantization: "fp8",
				reasoning: false,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "cerebras",
				test: "skip",
				externalId: "qwen-3-235b-a22b-instruct-2507",
				inputPrice: "0.6e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 262000,
				maxOutput: 8192,
				reasoning: false,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
				],
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3-235b-a22b-instruct-2507",
				inputPrice: "0.09e-6",
				outputPrice: "0.58e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 16384,
				quantization: "fp8",
				reasoning: false,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "vertex-openai",
				externalId: "qwen/qwen3-235b-a22b-instruct-2507-maas",
				deactivatedAt: new Date("2026-10-21"),
				inputPrice: "0.22e-6",
				outputPrice: "0.88e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				reasoning: false,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen3-235b-a22b-thinking-2507",
		name: "Qwen3 235B A22B Thinking 2507",
		description: "Qwen 3 MoE with reasoning capabilities.",
		family: "alibaba",
		releasedAt: new Date("2025-07-25"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen3-235B-A22B-Thinking-2507",
				// Endpoint removed upstream: 404 "The model does not exist"
				// (verified 2026-07-22)
				deactivatedAt: new Date("2026-07-22"),
				inputPrice: "0.2e-6",
				outputPrice: "0.6e-6",
				requestPrice: "0",
				contextSize: 262000,
				maxOutput: 8192,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				stability: "unstable",
				jsonOutput: true,
				// Nebius does not reliably support tool_choice
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3-235b-a22b-thinking-2507",
				inputPrice: "0.3e-6",
				outputPrice: "3e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				quantization: "fp8",
				reasoning: false,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
		],
	},
	{
		id: "qwen3-235b-a22b-fp8",
		name: "Qwen3 235B A22B FP8",
		description: "Qwen 3 235B MoE with FP8 quantization.",
		family: "alibaba",
		releasedAt: new Date("2025-04-28"),
		providers: [
			{
				stability: "unstable",
				providerId: "novita",
				externalId: "qwen/qwen3-235b-a22b-fp8",
				inputPrice: "0.2e-6",
				outputPrice: "0.8e-6",
				requestPrice: "0",
				contextSize: 40960,
				maxOutput: 20000,
				quantization: "fp8",
				reasoning: false,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen3-14b",
		name: "Qwen3 14B",
		description: "Mid-size Qwen 3 model.",
		family: "alibaba",
		releasedAt: new Date("2025-04-28"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen3-14B",
				inputPrice: "0.08e-6",
				outputPrice: "0.24e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2025-11-03"),
			},
		],
	},
	{
		id: "qwen3.5-9b",
		name: "Qwen3.5 9B",
		description:
			"Compact natively multimodal Qwen3.5 hybrid-reasoning model with vision, tool calling and a 262K context window.",
		family: "alibaba",
		releasedAt: new Date("2026-06-01"),
		providers: [
			{
				providerId: "deepinfra",
				externalId: "Qwen/Qwen3.5-9B",
				// deepinfra hangs on reasoning + streaming requests
				// (e2e 180s timeouts, verified 2026-07-21)
				stability: "unstable",
				inputPrice: "0.1e-6",
				outputPrice: "0.15e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				quantization: "bf16",
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "qwen3-32b",
		name: "Qwen3 32B",
		description: "Mid-size Qwen 3 model.",
		family: "alibaba",
		releasedAt: new Date("2025-04-28"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen3-32B",
				deactivatedAt: new Date("2026-08-31"),
				inputPrice: "0.1e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 40960,
				maxOutput: 8192,
				quantization: "fp8",
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "cerebras",
				externalId: "qwen-3-32b",
				inputPrice: "0.4e-6",
				outputPrice: "0.8e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
				],
				deactivatedAt: new Date("2026-02-16"),
			},
		],
	},
	{
		id: "qwen3-30b-a3b",
		name: "Qwen3 30B A3B",
		description: "Mid-size Qwen 3 MoE model.",
		family: "alibaba",
		releasedAt: new Date("2025-04-28"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen3-30B-A3B",
				inputPrice: "0.1e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2025-11-03"),
			},
		],
	},
	{
		id: "qwen25-coder-7b",
		name: "Qwen2.5 Coder 7B",
		description: "Qwen 2.5 code model, 7B size.",
		family: "alibaba",
		releasedAt: new Date("2024-09-19"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen2.5-Coder-7B-fast",
				inputPrice: "0.01e-6",
				outputPrice: "0.03e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: true,
				deactivatedAt: new Date("2026-04-25"),
			},
		],
	},
	{
		id: "qwen25-32b-instruct",
		name: "Qwen2.5 32B Instruct",
		description: "Qwen 2.5 instruction model, 32B size.",
		family: "alibaba",
		releasedAt: new Date("2024-09-19"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen2.5-32B-Instruct",
				inputPrice: "0.06e-6",
				outputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2025-09-10"),
			},
		],
	},
	{
		id: "qwen25-72b-instruct",
		name: "Qwen2.5 72B Instruct",
		description: "Qwen 2.5 instruction model, 72B size.",
		family: "alibaba",
		releasedAt: new Date("2024-09-19"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen2.5-72B-Instruct",
				inputPrice: "0.13e-6",
				outputPrice: "0.4e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2025-11-03"),
			},
		],
	},
	{
		id: "qwen2-vl-72b-instruct",
		name: "Qwen2 VL 72B Instruct",
		description: "Vision-language Qwen 2 model, 72B size.",
		family: "alibaba",
		releasedAt: new Date("2024-09-19"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen2-VL-72B-Instruct",
				inputPrice: "0.13e-6",
				outputPrice: "0.4e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 8192,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				deactivatedAt: new Date("2025-09-10"),
			},
		],
	},
	{
		id: "qwen2-5-vl-72b-instruct",
		name: "Qwen2.5 VL 72B Instruct",
		description: "Vision-language Qwen 2.5 model, 72B size.",
		family: "alibaba",
		releasedAt: new Date("2025-01-26"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen2.5-VL-72B-Instruct",
				deactivatedAt: new Date("2026-08-31"),
				inputPrice: "0.25e-6",
				outputPrice: "0.75e-6",
				requestPrice: "0",
				contextSize: 32000,
				maxOutput: 8192,
				quantization: "fp8",
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen3-vl-8b-instruct",
		name: "Qwen3 VL 8B Instruct",
		description: "Vision-language Qwen 3 model, 8B size.",
		family: "alibaba",
		releasedAt: new Date("2025-10-14"),
		providers: [
			{
				providerId: "novita",
				externalId: "qwen/qwen3-vl-8b-instruct",
				deactivatedAt: new Date("2026-07-01"),
				inputPrice: "0.08e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 8192,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen3-coder-480b-a35b-instruct",
		name: "Qwen3 Coder 480B A35B Instruct",
		description: "Code-specialized Qwen 3 MoE, 480B size.",
		family: "alibaba",
		releasedAt: new Date("2025-01-31"),
		providers: [
			{
				providerId: "nebius",
				stability: "unstable",
				externalId: "Qwen/Qwen3-Coder-480B-A35B-Instruct",
				// Endpoint removed upstream: 404 "The model does not exist"
				// (verified 2026-07-22)
				deactivatedAt: new Date("2026-07-22"),
				inputPrice: "0.4e-6",
				outputPrice: "1.8e-6",
				requestPrice: "0",
				contextSize: 262000,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				// Nebius does not reliably support tool_choice
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
			{
				providerId: "novita",
				stability: "unstable",
				externalId: "qwen/qwen3-coder-480b-a35b-instruct",
				inputPrice: "0.38e-6",
				outputPrice: "1.55e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 65536,
				quantization: "fp8",
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "vertex-openai",
				externalId: "qwen/qwen3-coder-480b-a35b-instruct-maas",
				deactivatedAt: new Date("2026-10-21"),
				inputPrice: "0.22e-6",
				cachedInputPrice: "0.022e-6",
				outputPrice: "1.8e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 65536,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen3-coder-30b-a3b-instruct",
		name: "Qwen3 Coder 30B A3B Instruct",
		description: "Code-specialized Qwen 3 MoE, 30B size.",
		family: "alibaba",
		releasedAt: new Date("2025-07-31"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen3-Coder-30B-A3B-Instruct",
				inputPrice: "0.1e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 262000,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-04-25"),
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3-coder-30b-a3b-instruct",
				inputPrice: "0.07e-6",
				outputPrice: "0.27e-6",
				requestPrice: "0",
				contextSize: 160000,
				maxOutput: 32768,
				quantization: "fp8",
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen3-30b-a3b-instruct-2507",
		name: "Qwen3 30B A3B Instruct 2507",
		description: "Qwen 3 MoE instruction model, July 2025.",
		family: "alibaba",
		releasedAt: new Date("2025-07-30"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen3-30B-A3B-Instruct-2507",
				deactivatedAt: new Date("2026-08-31"),
				inputPrice: "0.1e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 262000,
				maxOutput: 8192,
				quantization: "fp8",
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				// Nebius does not reliably support tool_choice
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
		],
	},
	{
		id: "qwen3-30b-a3b-thinking-2507",
		name: "Qwen3 30B A3B Thinking 2507",
		description: "Qwen 3 MoE with reasoning, July 2025.",
		family: "alibaba",
		releasedAt: new Date("2025-07-30"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen3-30B-A3B-Thinking-2507",
				inputPrice: "0.1e-6",
				outputPrice: "0.3e-6",
				requestPrice: "0",
				contextSize: 262000,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: true,
				// Nebius does not reliably support tool_choice
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
				deactivatedAt: new Date("2026-04-25"),
			},
		],
	},
	{
		id: "qwen-vl-max",
		name: "Qwen VL Max",
		description: "Vision-language Qwen flagship model.",
		family: "alibaba",
		releasedAt: new Date("2025-02-01"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen-vl-max",
				inputPrice: "0.8e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32000,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-13"),
			},
		],
	},
	{
		id: "qwen-vl-plus",
		name: "Qwen VL Plus",
		description: "Vision-language Qwen mid-tier model.",
		family: "alibaba",
		releasedAt: new Date("2025-02-05"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen-vl-plus",
				inputPrice: "0.21e-6",
				outputPrice: "0.64e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32000,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-13"),
			},
		],
	},
	{
		id: "qwen3-next-80b-a3b-thinking",
		name: "Qwen3 Next 80B A3B Thinking",
		description: "Preview Qwen 3 Next with reasoning.",
		family: "alibaba",
		releasedAt: new Date("2025-09-10"),
		providers: [
			{
				providerId: "alibaba",
				stability: "unstable",
				externalId: "qwen3-next-80b-a3b-thinking",
				inputPrice: "0.5e-6",
				outputPrice: "6e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				reasoning: true,
				reasoningMaxTokens: true,
				reasoningEfforts: ["minimal", "low", "medium", "high", "xhigh", "max"],
				reasoningOutput: "omit",
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
				deactivatedAt: new Date("2026-07-08"),
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3-next-80b-a3b-thinking",
				deactivatedAt: new Date("2026-07-01"),
				inputPrice: "0.15e-6",
				outputPrice: "1.5e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen3-Next-80B-A3B-Thinking",
				deactivatedAt: new Date("2026-08-31"),
				inputPrice: "0.15e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				quantization: "fp8",
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
			{
				providerId: "vertex-openai",
				externalId: "qwen/qwen3-next-80b-a3b-thinking-maas",
				deactivatedAt: new Date("2026-10-21"),
				// Vertex MaaS throttles this model's tiny concurrency quota (429
				// RESOURCE_EXHAUSTED) even on single requests, flaking e2e
				stability: "unstable",
				inputPrice: "0.15e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				reasoning: true,
				reasoningOutput: "omit",
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
		],
	},
	{
		id: "qwen3-next-80b-a3b-instruct",
		name: "Qwen3 Next 80B A3B Instruct",
		description: "Preview Qwen 3 Next instruction model.",
		family: "alibaba",
		releasedAt: new Date("2025-09-10"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3-next-80b-a3b-instruct",
				inputPrice: "0.5e-6",
				outputPrice: "2e-6",
				requestPrice: "0",
				contextSize: 129024,
				maxOutput: 32768,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-08"),
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3-next-80b-a3b-instruct",
				inputPrice: "0.15e-6",
				outputPrice: "1.5e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				quantization: "bf16",
				streaming: true,
				vision: false,
				tools: true,
				// novita rejects response_format json_object for this model (400)
				jsonOutput: false,
			},
			{
				providerId: "vertex-openai",
				externalId: "qwen/qwen3-next-80b-a3b-instruct-maas",
				deactivatedAt: new Date("2026-10-21"),
				inputPrice: "0.15e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen3-max",
		name: "Qwen3 Max",
		description: "Top-tier Qwen 3 with reasoning and vision support.",
		family: "alibaba",
		releasedAt: new Date("2025-09-24"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen3-max-preview",
				inputPrice: "1.2e-6",
				outputPrice: "6e-6",
				cachedInputPrice: "0.24e-6",
				cacheReadInputPrice: "0.12e-6",
				cacheWriteInputPrice: "1.5e-6",
				pricingTiers: [
					{
						name: "Up to 32K",
						upToTokens: 32000,
						inputPrice: "1.2e-6",
						outputPrice: "6e-6",
						cachedInputPrice: "0.24e-6",
						cacheReadInputPrice: "0.12e-6",
						cacheWriteInputPrice: "1.5e-6",
					},
					{
						name: "32K-128K",
						upToTokens: 128000,
						inputPrice: "2.4e-6",
						outputPrice: "12e-6",
						cachedInputPrice: "0.48e-6",
						cacheReadInputPrice: "0.24e-6",
						cacheWriteInputPrice: "3e-6",
					},
					{
						name: "128K-256K",
						upToTokens: 256000,
						inputPrice: "3e-6",
						outputPrice: "15e-6",
						cachedInputPrice: "0.6e-6",
						cacheReadInputPrice: "0.3e-6",
						cacheWriteInputPrice: "3.75e-6",
					},
				],
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: 32800,
				reasoning: true,
				reasoningMaxTokens: true,
				reasoningOutput: "omit",
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-10-10"),
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3-max",
				// Novita tiers this model by prompt length; the flat fields below are
				// the lowest tier for routing comparisons, actual billing uses
				// pricingTiers (verified live 2026-08-18).
				inputPrice: "0.845e-6",
				outputPrice: "3.38e-6",
				pricingTiers: [
					{
						name: "Up to 32,768 tokens",
						upToTokens: 32768,
						inputPrice: "0.845e-6",
						outputPrice: "3.38e-6",
					},
					{
						name: "32,768–131,072 tokens",
						upToTokens: 131072,
						inputPrice: "1.4e-6",
						outputPrice: "5.64e-6",
					},
					{
						name: "Over 131,072 tokens",
						upToTokens: Infinity,
						inputPrice: "2.11e-6",
						outputPrice: "8.45e-6",
					},
				],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 65536,
				quantization: "fp8",
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				// novita rejects tool_choice "required" and named function choices
				// with invalid_request_error; "auto" and "none" work
				supportedToolChoices: ["auto", "none"],
			},
		],
	},
	{
		id: "qwen3.7-max",
		name: "Qwen3.7 Max",
		description:
			"Largest and most capable model in the Qwen3.7 series, a next-generation agent-centric flagship excelling at programming and long-horizon autonomous execution.",
		family: "alibaba",
		releasedAt: new Date("2026-05-20"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.7-max",
				inputPrice: "2.5e-6",
				outputPrice: "7.5e-6",
				cachedInputPrice: "0.5e-6",
				cacheReadInputPrice: "0.25e-6",
				cacheWriteInputPrice: "3.125e-6",
				regions: [
					{ id: "singapore" },
					{
						id: "eu-frankfurt",
						inputPrice: "1.65e-6",
						outputPrice: "4.951e-6",
						cachedInputPrice: "0.33e-6",
						cacheReadInputPrice: "0.165e-6",
						cacheWriteInputPrice: "2.0625e-6",
					},
					{
						id: "us-virginia",
						inputPrice: "1.65e-6",
						outputPrice: "4.951e-6",
						cachedInputPrice: "0.33e-6",
						cacheReadInputPrice: "0.165e-6",
						cacheWriteInputPrice: "2.0625e-6",
					},
					{
						id: "cn-beijing",
						inputPrice: "1.65e-6",
						outputPrice: "4.951e-6",
						cachedInputPrice: "0.33e-6",
						cacheReadInputPrice: "0.165e-6",
						cacheWriteInputPrice: "2.0625e-6",
					},
				],
				requestPrice: "0",
				contextSize: 983616,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				reasoningOutput: "omit",
				streaming: true,
				vision: false,
				tools: true,
				webSearch: true,
				webSearchForcedOnly: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"tools",
					"response_format",
					"reasoning_effort",
				],
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3.7-max",
				inputPrice: "1.25e-6",
				cachedInputPrice: "0.25e-6",
				outputPrice: "3.75e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 65536,
				reasoning: true,
				reasoningOutput: "omit",
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"tools",
					"response_format",
				],
			},
		],
	},
	{
		id: "qwen3.7-plus",
		name: "Qwen3.7 Plus",
		description:
			"Mid-tier model in the Qwen3.7 series balancing speed, cost, and capability with strong coding and agentic performance.",
		family: "alibaba",
		releasedAt: new Date("2026-05-20"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.7-plus",
				inputPrice: "0.4e-6",
				outputPrice: "1.6e-6",
				cachedInputPrice: "0.08e-6",
				cacheReadInputPrice: "0.04e-6",
				cacheWriteInputPrice: "0.5e-6",
				pricingTiers: [
					{
						name: "Up to 256K",
						upToTokens: 256000,
						inputPrice: "0.4e-6",
						outputPrice: "1.6e-6",
						cachedInputPrice: "0.08e-6",
						cacheReadInputPrice: "0.04e-6",
						cacheWriteInputPrice: "0.5e-6",
					},
					{
						name: "Over 256K",
						upToTokens: Infinity,
						inputPrice: "1.2e-6",
						outputPrice: "4.8e-6",
						cachedInputPrice: "0.24e-6",
						cacheReadInputPrice: "0.12e-6",
						cacheWriteInputPrice: "1.5e-6",
					},
				],
				regions: [
					{ id: "singapore" },
					{
						id: "eu-frankfurt",
						inputPrice: "0.276e-6",
						outputPrice: "1.101e-6",
						cachedInputPrice: "0.0552e-6",
						cacheReadInputPrice: "0.0276e-6",
						cacheWriteInputPrice: "0.345e-6",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.276e-6",
								outputPrice: "1.101e-6",
								cachedInputPrice: "0.0552e-6",
								cacheReadInputPrice: "0.0276e-6",
								cacheWriteInputPrice: "0.345e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "0.826e-6",
								outputPrice: "3.301e-6",
								cachedInputPrice: "0.1652e-6",
								cacheReadInputPrice: "0.0826e-6",
								cacheWriteInputPrice: "1.0325e-6",
							},
						],
					},
					{
						id: "us-virginia",
						inputPrice: "0.276e-6",
						outputPrice: "1.101e-6",
						cachedInputPrice: "0.0552e-6",
						cacheReadInputPrice: "0.0276e-6",
						cacheWriteInputPrice: "0.345e-6",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.276e-6",
								outputPrice: "1.101e-6",
								cachedInputPrice: "0.0552e-6",
								cacheReadInputPrice: "0.0276e-6",
								cacheWriteInputPrice: "0.345e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "0.826e-6",
								outputPrice: "3.301e-6",
								cachedInputPrice: "0.1652e-6",
								cacheReadInputPrice: "0.0826e-6",
								cacheWriteInputPrice: "1.0325e-6",
							},
						],
					},
					{
						id: "cn-beijing",
						inputPrice: "0.276e-6",
						outputPrice: "1.101e-6",
						cachedInputPrice: "0.0552e-6",
						cacheReadInputPrice: "0.0276e-6",
						cacheWriteInputPrice: "0.345e-6",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.276e-6",
								outputPrice: "1.101e-6",
								cachedInputPrice: "0.0552e-6",
								cacheReadInputPrice: "0.0276e-6",
								cacheWriteInputPrice: "0.345e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "0.826e-6",
								outputPrice: "3.301e-6",
								cachedInputPrice: "0.1652e-6",
								cacheReadInputPrice: "0.0826e-6",
								cacheWriteInputPrice: "1.0325e-6",
							},
						],
					},
				],
				requestPrice: "0",
				contextSize: 983616,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				reasoningOutput: "omit",
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"tools",
					"response_format",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "qwen3.7-flash",
		name: "Qwen3.7 Flash",
		description:
			"Fast, cost-effective Qwen3.7 model with hybrid reasoning and tool calling for high-volume tasks.",
		family: "alibaba",
		releasedAt: new Date("2026-07-27"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.7-flash",
				inputPrice: "0.03e-6",
				outputPrice: "0.13e-6",
				cachedInputPrice: "0.006e-6",
				cacheReadInputPrice: "0.003e-6",
				cacheWriteInputPrice: "0.0375e-6",
				pricingTiers: [
					{
						name: "Up to 32K",
						upToTokens: 32000,
						inputPrice: "0.03e-6",
						outputPrice: "0.13e-6",
						cachedInputPrice: "0.006e-6",
						cacheReadInputPrice: "0.003e-6",
						cacheWriteInputPrice: "0.0375e-6",
					},
					{
						name: "32K-256K",
						upToTokens: 256000,
						inputPrice: "0.10e-6",
						outputPrice: "0.40e-6",
						cachedInputPrice: "0.02e-6",
						cacheReadInputPrice: "0.01e-6",
						cacheWriteInputPrice: "0.125e-6",
					},
					{
						name: "256K-1M",
						upToTokens: 1000000,
						inputPrice: "0.20e-6",
						outputPrice: "0.80e-6",
						cachedInputPrice: "0.04e-6",
						cacheReadInputPrice: "0.02e-6",
						cacheWriteInputPrice: "0.25e-6",
					},
				],
				regions: [
					{ id: "singapore" },
					{
						id: "eu-frankfurt",
						inputPrice: "0.028e-6",
						outputPrice: "0.110e-6",
						cachedInputPrice: "0.0056e-6",
						cacheReadInputPrice: "0.0028e-6",
						cacheWriteInputPrice: "0.035e-6",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.028e-6",
								outputPrice: "0.110e-6",
								cachedInputPrice: "0.0056e-6",
								cacheReadInputPrice: "0.0028e-6",
								cacheWriteInputPrice: "0.035e-6",
							},
							{
								name: "32K-256K",
								upToTokens: 256000,
								inputPrice: "0.083e-6",
								outputPrice: "0.330e-6",
								cachedInputPrice: "0.0166e-6",
								cacheReadInputPrice: "0.0083e-6",
								cacheWriteInputPrice: "0.10375e-6",
							},
							{
								name: "256K-1M",
								upToTokens: 1000000,
								inputPrice: "0.165e-6",
								outputPrice: "0.660e-6",
								cachedInputPrice: "0.033e-6",
								cacheReadInputPrice: "0.0165e-6",
								cacheWriteInputPrice: "0.20625e-6",
							},
						],
					},
					{
						id: "us-virginia",
						inputPrice: "0.028e-6",
						outputPrice: "0.110e-6",
						cachedInputPrice: "0.0056e-6",
						cacheReadInputPrice: "0.0028e-6",
						cacheWriteInputPrice: "0.035e-6",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.028e-6",
								outputPrice: "0.110e-6",
								cachedInputPrice: "0.0056e-6",
								cacheReadInputPrice: "0.0028e-6",
								cacheWriteInputPrice: "0.035e-6",
							},
							{
								name: "32K-256K",
								upToTokens: 256000,
								inputPrice: "0.083e-6",
								outputPrice: "0.330e-6",
								cachedInputPrice: "0.0166e-6",
								cacheReadInputPrice: "0.0083e-6",
								cacheWriteInputPrice: "0.10375e-6",
							},
							{
								name: "256K-1M",
								upToTokens: 1000000,
								inputPrice: "0.165e-6",
								outputPrice: "0.660e-6",
								cachedInputPrice: "0.033e-6",
								cacheReadInputPrice: "0.0165e-6",
								cacheWriteInputPrice: "0.20625e-6",
							},
						],
					},
					{
						id: "cn-beijing",
						inputPrice: "0.028e-6",
						outputPrice: "0.110e-6",
						cachedInputPrice: "0.0056e-6",
						cacheReadInputPrice: "0.0028e-6",
						cacheWriteInputPrice: "0.035e-6",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.028e-6",
								outputPrice: "0.110e-6",
								cachedInputPrice: "0.0056e-6",
								cacheReadInputPrice: "0.0028e-6",
								cacheWriteInputPrice: "0.035e-6",
							},
							{
								name: "32K-256K",
								upToTokens: 256000,
								inputPrice: "0.083e-6",
								outputPrice: "0.330e-6",
								cachedInputPrice: "0.0166e-6",
								cacheReadInputPrice: "0.0083e-6",
								cacheWriteInputPrice: "0.10375e-6",
							},
							{
								name: "256K-1M",
								upToTokens: 1000000,
								inputPrice: "0.165e-6",
								outputPrice: "0.660e-6",
								cachedInputPrice: "0.033e-6",
								cacheReadInputPrice: "0.0165e-6",
								cacheWriteInputPrice: "0.20625e-6",
							},
						],
					},
				],
				requestPrice: "0",
				contextSize: 983616,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				reasoningOutput: "omit",
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"tools",
					"response_format",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "qwen3.8-max",
		name: "Qwen3.8 Max",
		description:
			"Flagship 2.4-trillion-parameter MoE model in the Qwen3.8 series with native visual understanding, excelling at coding, professional work, and long-horizon autonomous agent tasks.",
		family: "alibaba",
		releasedAt: new Date("2026-08-02"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.8-max",
				inputPrice: "2e-6",
				outputPrice: "6e-6",
				// Alibaba publishes off-ratio cache rates for this model: implicit hits
				// are 0.125x and explicit hits 0.085x of input, not the usual 0.20x/0.10x
				// (cache creation still follows 1.25x). Do not "correct" these to the ratio.
				cachedInputPrice: "0.25e-6",
				cacheReadInputPrice: "0.17e-6",
				cacheWriteInputPrice: "2.5e-6",
				regions: [
					{ id: "singapore" },
					{ id: "eu-frankfurt" },
					{ id: "us-virginia" },
					{ id: "cn-beijing" },
				],
				requestPrice: "0",
				contextSize: 983616,
				maxOutput: 131072,
				reasoning: true,
				reasoningMaxTokens: true,
				reasoningOutput: "omit",
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchForcedOnly: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"tools",
					"response_format",
					"reasoning_effort",
				],
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3.8-max",
				inputPrice: "2e-6",
				cachedInputPrice: "0.25e-6",
				outputPrice: "6e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				// novita accepts every reasoning_effort tier but none of them changes
				// the deployment's behaviour (thinking stays on even for "none"), so
				// no tier is declared and reasoning_effort is left out of
				// supportedParameters below
				reasoning: true,
				reasoningOutput: "omit",
				streaming: true,
				vision: true,
				tools: true,
				// Qwen thinking models reject tool_choice "required" or object
				supportedToolChoices: ["auto", "none"],
				jsonOutput: true,
				jsonOutputSchema: true,
				// novita's deployment 400s on the developer role
				supportsDeveloperRole: false,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"tools",
					"tool_choice",
					"response_format",
				],
			},
		],
	},
	{
		id: "qwen3.8-27b",
		name: "Qwen3.8 27B",
		description:
			"Dense 27-billion-parameter Qwen3.8 model for coding, professional work, research, and long-horizon agentic tasks.",
		family: "alibaba",
		releasedAt: new Date("2026-08-14"),
		providers: [
			{
				providerId: "novita",
				externalId: "qwen/qwen3.8-27b",
				inputPrice: "0.42e-6",
				cachedInputPrice: "0.085e-6",
				outputPrice: "3e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				supportedToolChoices: ["auto", "none", "function"],
				jsonOutput: true,
				supportsDeveloperRole: false,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"tools",
					"tool_choice",
					"response_format",
				],
			},
			{
				providerId: "deepinfra",
				externalId: "Qwen/Qwen3.8-27B",
				// DeepInfra only serves a cache hit from about the third identical
				// prefix onwards, so cache-priced traffic cannot rely on it.
				deactivatedAt: new Date("2026-09-23"),
				inputPrice: "0.2e-6",
				cachedInputPrice: "0.05e-6",
				outputPrice: "2.5e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 235929,
				quantization: "bf16",
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high"],
				vision: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "qwen3.8-2.4t-a95b",
		name: "Qwen3.8 2.4T A95B",
		description:
			"Open-weight sparse MoE variant of Qwen3.8 Max with 95B active out of 2.4T total parameters.",
		family: "alibaba",
		releasedAt: new Date("2026-08-12"),
		providers: [
			{
				providerId: "together-ai",
				externalId: "Qwen/Qwen3.8-2.4T-A95B",
				inputPrice: "2e-6",
				cachedInputPrice: "0.25e-6",
				outputPrice: "6e-6",
				requestPrice: "0",
				contextSize: 1010000,
				maxOutput: 909000,
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "xhigh"],
				requiresDisableThinkingParam: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "deepinfra",
				externalId: "Qwen/Qwen3.8-2.4T-A95B",
				inputPrice: "2e-6",
				cachedInputPrice: "0.2e-6",
				outputPrice: "6e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 131072,
				quantization: "fp4",
				maxTemperature: 1,
				minCacheableTokens: 4096,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high", "xhigh", "max"],
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
				deactivatedAt: new Date("2026-10-12"),
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3.8-2.4t-a95b",
				inputPrice: "2e-6",
				cachedInputPrice: "0.25e-6",
				outputPrice: "6e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				supportedToolChoices: ["auto", "none"],
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "qwen3.8-flash",
		name: "Qwen3.8 Flash",
		description:
			"Fast, cost-effective Qwen3.8 model with hybrid reasoning, visual understanding and a 1M context for high-volume tasks.",
		family: "alibaba",
		releasedAt: new Date("2026-08-26"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.8-flash",
				inputPrice: "0.15e-6",
				outputPrice: "0.47e-6",
				// Alibaba publishes off-ratio cache rates for this model: both implicit
				// and explicit hits are 0.107x of input and creation is 1.33x, not the
				// usual 0.20x/0.10x/1.25x. Do not "correct" these to the ratio.
				cachedInputPrice: "0.016e-6",
				cacheReadInputPrice: "0.016e-6",
				cacheWriteInputPrice: "0.2e-6",
				regions: [{ id: "singapore" }],
				requestPrice: "0",
				contextSize: 983616,
				maxOutput: 131072,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				reasoningOutput: "omit",
				streaming: true,
				vision: true,
				tools: true,
				// The deployment 400s on "required" and named-function tool_choice with
				// "The tool_choice parameter does not support being set to required or
				// object in thinking mode", but honours both once thinking is off.
				supportedToolChoices: ["auto", "none"],
				supportedToolChoicesWithThinkingDisabled: ["required", "function"],
				webSearch: true,
				webSearchForcedOnly: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsDeveloperRole: false,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"tools",
					"tool_choice",
					"response_format",
					"reasoning_effort",
				],
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3.8-flash",
				inputPrice: "0.15e-6",
				cachedInputPrice: "0.016e-6",
				outputPrice: "0.47e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				reasoning: true,
				streaming: true,
				vision: false,
				tools: true,
				supportedToolChoices: ["auto", "none"],
				jsonOutput: true,
				jsonOutputSchema: true,
				supportsDeveloperRole: false,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"tools",
					"tool_choice",
					"response_format",
				],
			},
		],
	},
	{
		id: "qwen3-coder-next",
		name: "Qwen3 Coder Next",
		description:
			"Alibaba's Qwen3 Coder Next model optimized for coding tasks with 262K context.",
		family: "alibaba",
		releasedAt: new Date("2026-02-04"),
		providers: [
			{
				providerId: "embercloud",
				externalId: "qwen3-coder-next",
				inputPrice: "0.108e-6",
				outputPrice: "0.675e-6",
				cachedInputPrice: "0.06e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				streaming: true,
				vision: false,
				tools: true,
				// Embercloud leaks reasoning tags into JSON output, breaking json_object mode
				jsonOutput: false,
				// Embercloud bug: tool_choice other than "auto" breaks streaming tool calls for this model
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"tools",
					"parallel_tool_calls",
				],
			},
		],
	},
	{
		id: "qwen3-vl-30b-a3b-instruct",
		name: "Qwen3 VL 30B A3B Instruct",
		description: "Vision-language Qwen 3 MoE model, 30B size.",
		family: "alibaba",
		releasedAt: new Date("2025-10-05"),
		providers: [
			{
				providerId: "novita",
				externalId: "qwen/qwen3-vl-30b-a3b-instruct",
				inputPrice: "0.2e-6",
				outputPrice: "0.7e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				quantization: "bf16",
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: false,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"response_format",
					"tools",
					"tool_choice",
				],
			},
			{
				providerId: "deepinfra",
				externalId: "Qwen/Qwen3-VL-30B-A3B-Instruct",
				inputPrice: "0.15e-6",
				outputPrice: "0.60e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				quantization: "fp8",
				streaming: true,
				vision: true,
				tools: true,
				// deepinfra appends a stray ] to tool call arguments on
				// tool_choice "required" (~53% of calls malformed), so
				// "required" coerces to "auto" via supportedToolChoices
				supportedToolChoices: ["auto", "none", "function"],
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"response_format",
					"tools",
					"tool_choice",
				],
			},
		],
	},
	{
		id: "qwen3-vl-30b-a3b-thinking",
		name: "Qwen3 VL 30B A3B Thinking",
		description: "Vision-language Qwen 3 MoE with reasoning capabilities.",
		family: "alibaba",
		releasedAt: new Date("2025-10-11"),
		providers: [
			{
				providerId: "novita",
				externalId: "qwen/qwen3-vl-30b-a3b-thinking",
				deactivatedAt: new Date("2026-07-01"),
				inputPrice: "0.2e-6",
				outputPrice: "1e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				reasoning: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen3-32b-fp8",
		name: "Qwen3 32B FP8",
		description: "Qwen 3 32B with FP8 quantization.",
		family: "alibaba",
		releasedAt: new Date("2025-04-28"),
		providers: [
			{
				providerId: "novita",
				externalId: "qwen/qwen3-32b-fp8",
				inputPrice: "0.1e-6",
				outputPrice: "0.45e-6",
				requestPrice: "0",
				contextSize: 40960,
				maxOutput: 20000,
				quantization: "fp8",
				reasoning: false,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: false,
				deactivatedAt: new Date("2026-06-05"),
			},
		],
	},
	{
		id: "qwen3-30b-a3b-fp8",
		name: "Qwen3 30B A3B FP8",
		description: "Qwen 3 30B MoE with FP8 quantization.",
		family: "alibaba",
		releasedAt: new Date("2025-04-28"),
		providers: [
			{
				providerId: "novita",
				externalId: "qwen/qwen3-30b-a3b-fp8",
				inputPrice: "0.09e-6",
				outputPrice: "0.45e-6",
				requestPrice: "0",
				contextSize: 40960,
				maxOutput: 20000,
				quantization: "fp8",
				reasoning: false,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: false,
				deactivatedAt: new Date("2026-06-05"),
			},
		],
	},
	{
		id: "qwen3-4b-fp8",
		name: "Qwen3 4B FP8",
		description: "Lightweight Qwen 3 4B with FP8 quantization.",
		family: "alibaba",
		releasedAt: new Date("2025-04-28"),
		providers: [
			{
				providerId: "novita",
				externalId: "qwen/qwen3-4b-fp8",
				deactivatedAt: new Date("2026-07-11"),
				inputPrice: "0.03e-6",
				outputPrice: "0.03e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 20000,
				quantization: "fp8",
				reasoning: false,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: false,
			},
		],
	},
	{
		id: "qwen35-397b-a17b",
		name: "Qwen3.5 397B A17B",
		description:
			"Native multimodal MoE model with 397B total / 17B active parameters, reasoning, and agent capabilities.",
		family: "alibaba",
		releasedAt: new Date("2026-02-16"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.5-397b-a17b",
				inputPrice: "0.6e-6",
				outputPrice: "3.6e-6",
				// No eu-frankfurt: the model answers there, but takes ~120s even for a
				// single token and returns garbage content, while other models on the
				// same host reply in ~2s. Tool calls and JSON output never return.
				regions: [
					{ id: "singapore" },
					{ id: "us-virginia" },
					{
						id: "cn-beijing",
						inputPrice: "0.172e-6",
						outputPrice: "1.032e-6",
						pricingTiers: [
							{
								name: "Up to 128K",
								upToTokens: 128000,
								inputPrice: "0.172e-6",
								outputPrice: "1.032e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.43e-6",
								outputPrice: "2.58e-6",
							},
						],
					},
				],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchForcedOnly: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
					"reasoning_effort",
				],
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3.5-397b-a17b",
				inputPrice: "0.6e-6",
				outputPrice: "3.6e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 64000,
				reasoning: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen3.5-397B-A17B",
				// Intermittent 404 "The model does not exist" in production
				// (~57% success rate observed 2026-07-14)
				deactivatedAt: new Date("2026-07-14"),
				inputPrice: "0.6e-6",
				outputPrice: "3.6e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				quantization: "fp4",
				reasoning: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
		],
	},
	{
		id: "qwen-image-3.0",
		name: "Qwen Image 3.0",
		description:
			"Alibaba's third-generation Qwen image model for text-to-image generation and image editing with strong text rendering.",
		family: "alibaba",
		output: ["text", "image"],
		releasedAt: new Date("2026-08-05"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-image-3.0",
				inputPrice: "0",
				outputPrice: "0",
				// Every resolution bills the same rate, so a single "default" tier
				// covers all sizes; add explicit tier keys only if pricing splits.
				perImagePrice: {
					default: "0.03",
				},
				contextSize: 4500,
				maxOutput: 4096,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "qwen-image-3.0-pro",
		name: "Qwen Image 3.0 Pro",
		description:
			"Alibaba's flagship third-generation Qwen image model for highest quality text-to-image generation and image editing.",
		family: "alibaba",
		output: ["text", "image"],
		releasedAt: new Date("2026-08-05"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-image-3.0-pro",
				inputPrice: "0",
				outputPrice: "0",
				// The tier is billed on the served resolution DashScope reports in
				// usage.output_image_type; "default" covers requests without a size,
				// where the model auto-picks a resolution and serves 2K.
				perImagePrice: {
					"1K": "0.04",
					"2K": "0.075",
					default: "0.075",
				},
				contextSize: 4500,
				maxOutput: 4096,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "qwen-image-2.1-pro",
		name: "Qwen Image 2.1 Pro",
		description:
			"Alibaba's Qwen Image 2.1 Pro model for text-to-image generation and multi-image editing with native transparent output.",
		family: "alibaba",
		output: ["text", "image"],
		releasedAt: new Date("2026-09-20"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-image-2.1-pro",
				inputPrice: "0",
				outputPrice: "0",
				perImagePrice: {
					default: "0.04",
				},
				contextSize: 4500,
				maxOutput: 4096,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "qwen-image-2.1-turbo",
		name: "Qwen Image 2.1 Turbo",
		description:
			"Alibaba's cost-effective Qwen Image 2.1 Turbo model for fast text-to-image generation and multi-image editing with native transparent output.",
		family: "alibaba",
		output: ["text", "image"],
		releasedAt: new Date("2026-10-09"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-image-2.1-turbo",
				inputPrice: "0",
				outputPrice: "0",
				perImagePrice: {
					default: "0.016",
				},
				contextSize: 4500,
				maxOutput: 4096,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "qwen-image-plus",
		name: "Qwen Image Plus",
		description:
			"Alibaba's Qwen Image Plus model for text-to-image generation with excellent text rendering capabilities.",
		family: "alibaba",
		output: ["text", "image"],
		releasedAt: new Date("2025-08-04"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-image-plus",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.03",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: false,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
				deactivatedAt: new Date("2026-10-10"),
			},
		],
	},
	{
		id: "qwen-image-max",
		name: "Qwen Image Max",
		description:
			"Alibaba's flagship Qwen Image Max model for highest quality text-to-image generation.",
		family: "alibaba",
		output: ["text", "image"],
		releasedAt: new Date("2025-08-04"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-image-max",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.075",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: false,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
				deactivatedAt: new Date("2026-10-10"),
			},
		],
	},
	{
		id: "qwen-image",
		name: "Qwen Image",
		description:
			"Alibaba's Qwen Image model for text-to-image generation with excellent text rendering capabilities.",
		family: "alibaba",
		output: ["text", "image"],
		releasedAt: new Date("2025-08-04"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-image",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.035",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: false,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
				deactivatedAt: new Date("2026-10-10"),
			},
		],
	},
	{
		id: "qwen-image-max-2025-12-30",
		name: "Qwen Image Max 2025-12-30",
		description:
			"Alibaba's Qwen 2025-12-30 Image model for text-to-image generation with excellent text rendering capabilities.",
		family: "alibaba",
		output: ["text", "image"],
		releasedAt: new Date("2025-12-31"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-image-max-2025-12-30",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.075",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: false,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
				deactivatedAt: new Date("2026-10-10"),
			},
		],
	},
	{
		id: "qwq-plus",
		name: "QwQ Plus",
		description:
			"Alibaba's reasoning model with enhanced math and code capabilities.",
		family: "alibaba",
		releasedAt: new Date("2025-03-06"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwq-plus",
				inputPrice: "0.8e-6",
				outputPrice: "2.4e-6",
				regions: [
					{ id: "singapore" },
					{
						id: "cn-beijing",
						inputPrice: "0.23e-6",
						outputPrice: "0.574e-6",
						// QwQ models are officially stream-only per Alibaba docs:
						// "Some models only support streaming calls: [...] the commercial
						// and open-source versions of QwQ"
						streaming: "only",
					},
				],
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 8192,
				reasoning: true,
				reasoningOutput: "omit",
				streaming: true,
				vision: false,
				tools: false,
				jsonOutput: false,
				deactivatedAt: new Date("2026-07-13"),
			},
		],
	},
	{
		id: "qwen-coder-plus",
		name: "Qwen Coder Plus",
		description: "Alibaba's code-specialized model based on Qwen2.5.",
		family: "alibaba",
		releasedAt: new Date("2024-09-18"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-coder-plus",
				inputPrice: "0.502e-6",
				outputPrice: "1.004e-6",
				regions: [{ id: "singapore" }, { id: "cn-beijing" }],
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-10-10"),
			},
		],
	},
	{
		id: "qwen3-coder-flash",
		name: "Qwen3 Coder Flash",
		description: "Fast, cost-effective Qwen 3 model for code generation.",
		family: "alibaba",
		releasedAt: new Date("2025-07-22"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3-coder-flash",
				inputPrice: "0.3e-6",
				outputPrice: "1.5e-6",
				cachedInputPrice: "0.06e-6",
				cacheReadInputPrice: "0.03e-6",
				cacheWriteInputPrice: "0.375e-6",
				regions: [
					{
						id: "singapore",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.3e-6",
								outputPrice: "1.5e-6",
								cachedInputPrice: "0.06e-6",
								cacheReadInputPrice: "0.03e-6",
								cacheWriteInputPrice: "0.375e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.5e-6",
								outputPrice: "2.5e-6",
								cachedInputPrice: "0.1e-6",
								cacheReadInputPrice: "0.05e-6",
								cacheWriteInputPrice: "0.625e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.8e-6",
								outputPrice: "4.0e-6",
								cachedInputPrice: "0.16e-6",
								cacheReadInputPrice: "0.08e-6",
								cacheWriteInputPrice: "1.0e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "1.6e-6",
								outputPrice: "9.6e-6",
								cachedInputPrice: "0.32e-6",
								cacheReadInputPrice: "0.16e-6",
								cacheWriteInputPrice: "2.0e-6",
							},
						],
					},
					{
						id: "eu-frankfurt",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.3e-6",
								outputPrice: "1.5e-6",
								cachedInputPrice: "0.06e-6",
								cacheReadInputPrice: "0.03e-6",
								cacheWriteInputPrice: "0.375e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.5e-6",
								outputPrice: "2.5e-6",
								cachedInputPrice: "0.1e-6",
								cacheReadInputPrice: "0.05e-6",
								cacheWriteInputPrice: "0.625e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.8e-6",
								outputPrice: "4.0e-6",
								cachedInputPrice: "0.16e-6",
								cacheReadInputPrice: "0.08e-6",
								cacheWriteInputPrice: "1.0e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "1.6e-6",
								outputPrice: "9.6e-6",
								cachedInputPrice: "0.32e-6",
								cacheReadInputPrice: "0.16e-6",
								cacheWriteInputPrice: "2.0e-6",
							},
						],
					},
					{
						id: "us-virginia",
						inputPrice: "0.144e-6",
						outputPrice: "0.574e-6",
						cachedInputPrice: "0.029e-6",
						cacheReadInputPrice: "0.0144e-6",
						cacheWriteInputPrice: "0.18e-6",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.144e-6",
								outputPrice: "0.574e-6",
								cachedInputPrice: "0.029e-6",
								cacheReadInputPrice: "0.0144e-6",
								cacheWriteInputPrice: "0.18e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.24e-6",
								outputPrice: "0.957e-6",
								cachedInputPrice: "0.048e-6",
								cacheReadInputPrice: "0.024e-6",
								cacheWriteInputPrice: "0.3e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.384e-6",
								outputPrice: "1.53e-6",
								cachedInputPrice: "0.077e-6",
								cacheReadInputPrice: "0.0384e-6",
								cacheWriteInputPrice: "0.48e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "0.768e-6",
								outputPrice: "3.67e-6",
								cachedInputPrice: "0.154e-6",
								cacheReadInputPrice: "0.0768e-6",
								cacheWriteInputPrice: "0.96e-6",
							},
						],
					},
					{
						id: "cn-beijing",
						inputPrice: "0.144e-6",
						outputPrice: "0.574e-6",
						cachedInputPrice: "0.029e-6",
						cacheReadInputPrice: "0.0144e-6",
						cacheWriteInputPrice: "0.18e-6",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.144e-6",
								outputPrice: "0.574e-6",
								cachedInputPrice: "0.029e-6",
								cacheReadInputPrice: "0.0144e-6",
								cacheWriteInputPrice: "0.18e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.216e-6",
								outputPrice: "0.861e-6",
								cachedInputPrice: "0.043e-6",
								cacheReadInputPrice: "0.0216e-6",
								cacheWriteInputPrice: "0.27e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.359e-6",
								outputPrice: "1.434e-6",
								cachedInputPrice: "0.072e-6",
								cacheReadInputPrice: "0.0359e-6",
								cacheWriteInputPrice: "0.44875e-6",
							},
							{
								name: "256K-1M",
								upToTokens: 1000000,
								inputPrice: "0.717e-6",
								outputPrice: "3.584e-6",
								cachedInputPrice: "0.143e-6",
								cacheReadInputPrice: "0.0717e-6",
								cacheWriteInputPrice: "0.89625e-6",
							},
						],
					},
				],
				requestPrice: "0",
				contextSize: 997952,
				maxOutput: 65536,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen3-vl-plus",
		name: "Qwen3 VL Plus",
		description: "Qwen 3 vision-language model with balanced performance.",
		family: "alibaba",
		releasedAt: new Date("2025-09-23"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3-vl-plus",
				inputPrice: "0.2e-6",
				outputPrice: "1.6e-6",
				cachedInputPrice: "0.04e-6",
				cacheReadInputPrice: "0.02e-6",
				cacheWriteInputPrice: "0.25e-6",
				regions: [
					{
						id: "singapore",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.2e-6",
								outputPrice: "1.6e-6",
								cachedInputPrice: "0.04e-6",
								cacheReadInputPrice: "0.02e-6",
								cacheWriteInputPrice: "0.25e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.3e-6",
								outputPrice: "2.4e-6",
								cachedInputPrice: "0.06e-6",
								cacheReadInputPrice: "0.03e-6",
								cacheWriteInputPrice: "0.375e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.6e-6",
								outputPrice: "4.8e-6",
								cachedInputPrice: "0.12e-6",
								cacheReadInputPrice: "0.06e-6",
								cacheWriteInputPrice: "0.75e-6",
							},
						],
					},
					{
						id: "eu-frankfurt",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.2e-6",
								outputPrice: "1.6e-6",
								cachedInputPrice: "0.04e-6",
								cacheReadInputPrice: "0.02e-6",
								cacheWriteInputPrice: "0.25e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.3e-6",
								outputPrice: "2.4e-6",
								cachedInputPrice: "0.06e-6",
								cacheReadInputPrice: "0.03e-6",
								cacheWriteInputPrice: "0.375e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.6e-6",
								outputPrice: "4.8e-6",
								cachedInputPrice: "0.12e-6",
								cacheReadInputPrice: "0.06e-6",
								cacheWriteInputPrice: "0.75e-6",
							},
						],
					},
					{
						id: "us-virginia",
						inputPrice: "0.143e-6",
						outputPrice: "1.434e-6",
						cachedInputPrice: "0.029e-6",
						cacheReadInputPrice: "0.0143e-6",
						cacheWriteInputPrice: "0.17875e-6",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.143e-6",
								outputPrice: "1.434e-6",
								cachedInputPrice: "0.029e-6",
								cacheReadInputPrice: "0.0143e-6",
								cacheWriteInputPrice: "0.17875e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.215e-6",
								outputPrice: "2.151e-6",
								cachedInputPrice: "0.043e-6",
								cacheReadInputPrice: "0.0215e-6",
								cacheWriteInputPrice: "0.26875e-6",
							},
							{
								name: "Over 128K",
								upToTokens: Infinity,
								inputPrice: "0.429e-6",
								outputPrice: "4.302e-6",
								cachedInputPrice: "0.086e-6",
								cacheReadInputPrice: "0.0429e-6",
								cacheWriteInputPrice: "0.53625e-6",
							},
						],
					},
					{
						id: "cn-beijing",
						inputPrice: "0.143e-6",
						outputPrice: "1.434e-6",
						cachedInputPrice: "0.029e-6",
						cacheReadInputPrice: "0.0143e-6",
						cacheWriteInputPrice: "0.17875e-6",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.143e-6",
								outputPrice: "1.434e-6",
								cachedInputPrice: "0.029e-6",
								cacheReadInputPrice: "0.0143e-6",
								cacheWriteInputPrice: "0.17875e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.215e-6",
								outputPrice: "2.15e-6",
								cachedInputPrice: "0.043e-6",
								cacheReadInputPrice: "0.0215e-6",
								cacheWriteInputPrice: "0.26875e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.43e-6",
								outputPrice: "4.301e-6",
								cachedInputPrice: "0.086e-6",
								cacheReadInputPrice: "0.043e-6",
								cacheWriteInputPrice: "0.5375e-6",
							},
						],
					},
				],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
			},
		],
	},
	{
		id: "qwen3-vl-flash",
		name: "Qwen3 VL Flash",
		description: "Fast Qwen 3 vision-language model for quick image tasks.",
		family: "alibaba",
		releasedAt: new Date("2025-10-15"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3-vl-flash",
				inputPrice: "0.05e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.01e-6",
				regions: [
					{
						id: "singapore",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.05e-6",
								outputPrice: "0.4e-6",
								cachedInputPrice: "0.01e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.075e-6",
								outputPrice: "0.6e-6",
								cachedInputPrice: "0.015e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.12e-6",
								outputPrice: "0.96e-6",
								cachedInputPrice: "0.024e-6",
							},
						],
					},
					{
						id: "us-virginia",
						inputPrice: "0.022e-6",
						outputPrice: "0.215e-6",
						cachedInputPrice: "0.0044e-6",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.022e-6",
								outputPrice: "0.215e-6",
								cachedInputPrice: "0.0044e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.033e-6",
								outputPrice: "0.323e-6",
								cachedInputPrice: "0.0066e-6",
							},
							{
								name: "Over 128K",
								upToTokens: Infinity,
								inputPrice: "0.053e-6",
								outputPrice: "0.516e-6",
								cachedInputPrice: "0.0106e-6",
							},
						],
					},
					{
						id: "cn-beijing",
						inputPrice: "0.022e-6",
						outputPrice: "0.215e-6",
						cachedInputPrice: "0.0044e-6",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.022e-6",
								outputPrice: "0.215e-6",
								cachedInputPrice: "0.0044e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.043e-6",
								outputPrice: "0.43e-6",
								cachedInputPrice: "0.0086e-6",
							},
							{
								name: "128K-256K",
								upToTokens: 256000,
								inputPrice: "0.086e-6",
								outputPrice: "0.859e-6",
								cachedInputPrice: "0.017e-6",
							},
						],
					},
				],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-10-10"),
			},
		],
	},
	{
		id: "qwen3-vl-235b-a22b-instruct",
		name: "Qwen3 VL 235B A22B Instruct",
		description: "Large Qwen 3 MoE vision-language instruction model.",
		family: "alibaba",
		releasedAt: new Date("2025-09-23"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3-vl-235b-a22b-instruct",
				inputPrice: "0.5e-6",
				outputPrice: "2.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-08"),
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3-vl-235b-a22b-instruct",
				inputPrice: "0.3e-6",
				outputPrice: "1.5e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "deepinfra",
				externalId: "Qwen/Qwen3-VL-235B-A22B-Instruct",
				inputPrice: "0.20e-6",
				outputPrice: "0.88e-6",
				cachedInputPrice: "0.11e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				quantization: "fp8",
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"stream",
					"tools",
					"response_format",
				],
			},
		],
	},
	{
		id: "qwen3-vl-235b-a22b-thinking",
		name: "Qwen3 VL 235B A22B Thinking",
		description:
			"Large Qwen 3 MoE vision-language model with reasoning capabilities.",
		family: "alibaba",
		releasedAt: new Date("2025-09-23"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3-vl-235b-a22b-thinking",
				inputPrice: "0.5e-6",
				outputPrice: "2.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				reasoning: true,
				reasoningOutput: "omit",
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: false,
				deactivatedAt: new Date("2026-07-08"),
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3-vl-235b-a22b-thinking",
				inputPrice: "0.98e-6",
				outputPrice: "3.95e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				reasoning: true,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: false,
			},
		],
	},
	{
		id: "qwen2-5-vl-32b-instruct",
		name: "Qwen2.5 VL 32B Instruct",
		description: "Vision-language Qwen 2.5 model, 32B size.",
		family: "alibaba",
		releasedAt: new Date("2025-02-19"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen2.5-vl-32b-instruct",
				inputPrice: "1.4e-6",
				outputPrice: "4.2e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutput: true,
				// Model Studio answers 403 access_denied on both hosts that serve it
				// (Singapore and Beijing) and 404s everywhere else, so no request can
				// succeed on this mapping.
				deactivatedAt: new Date("2026-08-11"),
			},
		],
	},
	{
		id: "qwen3-max-2026-01-23",
		name: "Qwen3 Max 2026-01-23",
		description:
			"Latest Qwen 3 flagship model with integrated thinking mode and tool support.",
		family: "alibaba",
		releasedAt: new Date("2026-01-23"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen3-max-2026-01-23",
				inputPrice: "1.2e-6",
				outputPrice: "6.0e-6",
				cachedInputPrice: "0.24e-6",
				cacheReadInputPrice: "0.12e-6",
				cacheWriteInputPrice: "1.5e-6",
				regions: [
					{
						id: "singapore",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "1.2e-6",
								outputPrice: "6.0e-6",
								cachedInputPrice: "0.24e-6",
								cacheReadInputPrice: "0.12e-6",
								cacheWriteInputPrice: "1.5e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "2.4e-6",
								outputPrice: "12.0e-6",
								cachedInputPrice: "0.48e-6",
								cacheReadInputPrice: "0.24e-6",
								cacheWriteInputPrice: "3.0e-6",
							},
							{
								name: "128K-252K",
								upToTokens: 252000,
								inputPrice: "3.0e-6",
								outputPrice: "15.0e-6",
								cachedInputPrice: "0.6e-6",
								cacheReadInputPrice: "0.3e-6",
								cacheWriteInputPrice: "3.75e-6",
							},
						],
					},
					{
						id: "us-virginia",
						inputPrice: "0.359e-6",
						outputPrice: "1.434e-6",
						cachedInputPrice: "0.072e-6",
						cacheReadInputPrice: "0.0359e-6",
						cacheWriteInputPrice: "0.44875e-6",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.359e-6",
								outputPrice: "1.434e-6",
								cachedInputPrice: "0.072e-6",
								cacheReadInputPrice: "0.0359e-6",
								cacheWriteInputPrice: "0.44875e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.718e-6",
								outputPrice: "2.868e-6",
								cachedInputPrice: "0.144e-6",
								cacheReadInputPrice: "0.0718e-6",
								cacheWriteInputPrice: "0.8975e-6",
							},
							{
								name: "Over 128K",
								upToTokens: Infinity,
								inputPrice: "0.898e-6",
								outputPrice: "3.585e-6",
								cachedInputPrice: "0.18e-6",
								cacheReadInputPrice: "0.0898e-6",
								cacheWriteInputPrice: "1.1225e-6",
							},
						],
					},
					{
						id: "cn-beijing",
						inputPrice: "0.359e-6",
						outputPrice: "1.434e-6",
						cachedInputPrice: "0.072e-6",
						cacheReadInputPrice: "0.0359e-6",
						cacheWriteInputPrice: "0.44875e-6",
						pricingTiers: [
							{
								name: "Up to 32K",
								upToTokens: 32000,
								inputPrice: "0.359e-6",
								outputPrice: "1.434e-6",
								cachedInputPrice: "0.072e-6",
								cacheReadInputPrice: "0.0359e-6",
								cacheWriteInputPrice: "0.44875e-6",
							},
							{
								name: "32K-128K",
								upToTokens: 128000,
								inputPrice: "0.574e-6",
								outputPrice: "2.294e-6",
								cachedInputPrice: "0.115e-6",
								cacheReadInputPrice: "0.0574e-6",
								cacheWriteInputPrice: "0.7175e-6",
							},
							{
								name: "128K-252K",
								upToTokens: 252000,
								inputPrice: "1.004e-6",
								outputPrice: "4.014e-6",
								cachedInputPrice: "0.201e-6",
								cacheReadInputPrice: "0.1004e-6",
								cacheWriteInputPrice: "1.255e-6",
							},
						],
					},
				],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 65536,
				reasoning: true,
				reasoningOutput: "omit",
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-07-08"),
			},
		],
	},
	{
		id: "qwen-image-edit-plus",
		name: "Qwen Image Edit Plus",
		description: "Alibaba's image editing model for modifying existing images.",
		family: "alibaba",
		output: ["text", "image"],
		imageInputRequired: true,
		releasedAt: new Date("2025-08-19"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-image-edit-plus",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.04",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
				deactivatedAt: new Date("2026-10-10"),
			},
		],
	},
	{
		id: "qwen-image-edit-max",
		name: "Qwen Image Edit Max",
		description:
			"Alibaba's flagship image editing model for high-quality image modifications.",
		family: "alibaba",
		output: ["text", "image"],
		imageInputRequired: true,
		releasedAt: new Date("2026-01-16"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-image-edit-max",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.08",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
				deactivatedAt: new Date("2026-10-10"),
			},
		],
	},
	{
		id: "qwen3.6-max-preview",
		name: "Qwen3.6 Max Preview",
		description:
			"Preview of Alibaba's largest Qwen3.6 model with enhanced vibe coding, coding agent execution, and front-end development skills. Plain-text only.",
		family: "alibaba",
		releasedAt: new Date("2026-04-29"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.6-max-preview",
				inputPrice: "1.3e-6",
				outputPrice: "7.8e-6",
				cachedInputPrice: "0.13e-6",
				regions: [{ id: "singapore" }],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 65536,
				reasoning: true,
				reasoningMaxTokens: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
					"reasoning_effort",
				],
				deactivatedAt: new Date("2026-10-10"),
			},
		],
	},
	{
		id: "qwen3.6-plus",
		name: "Qwen3.6 Plus",
		description:
			"Native vision-language Plus model with significantly improved agentic coding, front-end programming, vibe coding, multi-modal recognition, OCR, and object localization over the 3.5 series.",
		family: "alibaba",
		releasedAt: new Date("2026-04-29"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.6-plus",
				inputPrice: "0.5e-6",
				outputPrice: "3e-6",
				cachedInputPrice: "0.05e-6",
				cacheWriteInputPrice: "0.625e-6",
				pricingTiers: [
					{
						name: "Up to 256K",
						upToTokens: 256000,
						inputPrice: "0.5e-6",
						outputPrice: "3e-6",
						cachedInputPrice: "0.05e-6",
						cacheWriteInputPrice: "0.625e-6",
					},
					{
						name: "Over 256K",
						upToTokens: Infinity,
						inputPrice: "2e-6",
						outputPrice: "6e-6",
						cachedInputPrice: "0.2e-6",
						cacheWriteInputPrice: "2.5e-6",
					},
				],
				regions: [
					{ id: "singapore" },
					{
						id: "eu-frankfurt",
						inputPrice: "0.276e-6",
						outputPrice: "1.651e-6",
						cachedInputPrice: "0.0276e-6",
						cacheWriteInputPrice: "0.345e-6",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.276e-6",
								outputPrice: "1.651e-6",
								cachedInputPrice: "0.0276e-6",
								cacheWriteInputPrice: "0.345e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "1.101e-6",
								outputPrice: "6.602e-6",
								cachedInputPrice: "0.1101e-6",
								cacheWriteInputPrice: "1.37625e-6",
							},
						],
					},
					{
						id: "us-virginia",
						inputPrice: "0.276e-6",
						outputPrice: "1.651e-6",
						cachedInputPrice: "0.0276e-6",
						cacheWriteInputPrice: "0.345e-6",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.276e-6",
								outputPrice: "1.651e-6",
								cachedInputPrice: "0.0276e-6",
								cacheWriteInputPrice: "0.345e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "1.101e-6",
								outputPrice: "6.602e-6",
								cachedInputPrice: "0.1101e-6",
								cacheWriteInputPrice: "1.37625e-6",
							},
						],
					},
					{
						id: "cn-beijing",
						inputPrice: "0.276e-6",
						outputPrice: "1.651e-6",
						cachedInputPrice: "0.0276e-6",
						cacheWriteInputPrice: "0.345e-6",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.276e-6",
								outputPrice: "1.651e-6",
								cachedInputPrice: "0.0276e-6",
								cacheWriteInputPrice: "0.345e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "1.101e-6",
								outputPrice: "6.602e-6",
								cachedInputPrice: "0.1101e-6",
								cacheWriteInputPrice: "1.37625e-6",
							},
						],
					},
				],
				requestPrice: "0",
				contextSize: 983616,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchForcedOnly: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "qwen3.5-122b-a10b",
		name: "Qwen3.5 122B A10B",
		description:
			"Open-weight Qwen3.5 native vision-language MoE model (122B total, 10B active) with hybrid linear attention.",
		family: "alibaba",
		releasedAt: new Date("2026-02-26"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.5-122b-a10b",
				inputPrice: "0.4e-6",
				outputPrice: "3.2e-6",
				regions: [{ id: "singapore" }],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 65536,
				maxTemperature: 1,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
					"reasoning_effort",
				],
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3.5-122b-a10b",
				inputPrice: "0.4e-6",
				outputPrice: "3.2e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 65536,
				quantization: "bf16",
				maxTemperature: 1,
				reasoning: true,
				streaming: true,
				vision: true,
				requiresBase64Images: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
		],
	},
	{
		id: "qwen3.5-27b",
		name: "Qwen3.5 27B",
		description:
			"Open-weight dense Qwen3.5 native vision-language model with linear attention for fast responses.",
		family: "alibaba",
		releasedAt: new Date("2026-02-26"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.5-27b",
				inputPrice: "0.3e-6",
				outputPrice: "2.4e-6",
				regions: [{ id: "singapore" }],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 65536,
				maxTemperature: 1,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "qwen3.5-35b-a3b",
		name: "Qwen3.5 35B A3B",
		description:
			"Open-weight Qwen3.5 native vision-language MoE model (35B total, 3B active) with hybrid linear attention.",
		family: "alibaba",
		releasedAt: new Date("2026-02-26"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.5-35b-a3b",
				inputPrice: "0.25e-6",
				outputPrice: "2e-6",
				regions: [{ id: "singapore" }],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 65536,
				maxTemperature: 1,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "qwen3.6-35b-a3b",
		name: "Qwen3.6 35B A3B",
		description:
			"Native vision-language MoE model with hybrid linear attention and sparse mixture-of-experts, improved agentic coding, math/code reasoning, and spatial intelligence.",
		family: "alibaba",
		releasedAt: new Date("2026-04-29"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.6-35b-a3b",
				inputPrice: "0.375e-6",
				outputPrice: "2.25e-6",
				regions: [
					{ id: "singapore" },
					{
						id: "eu-frankfurt",
						inputPrice: "0.248e-6",
						outputPrice: "1.485e-6",
					},
					{
						id: "us-virginia",
						inputPrice: "0.248e-6",
						outputPrice: "1.485e-6",
					},
					{
						id: "cn-beijing",
						inputPrice: "0.248e-6",
						outputPrice: "1.485e-6",
					},
				],
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchForcedOnly: true,
				webSearchPrice: "0.01",
				jsonOutput: true,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
					"reasoning_effort",
				],
			},
			{
				providerId: "novita",
				externalId: "qwen/qwen3.6-35b-a3b",
				inputPrice: "0.248e-6",
				outputPrice: "1.485e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 64000,
				reasoning: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
				],
			},
		],
	},
	{
		id: "qwen3.6-flash",
		name: "Qwen3.6 Flash",
		description:
			"Fast, cost-effective Qwen3.6 model with native vision-language capabilities, hybrid reasoning, tool calling, and a 1M context window for high-volume tasks.",
		family: "alibaba",
		releasedAt: new Date("2026-04-29"),
		providers: [
			{
				providerId: "alibaba",
				externalId: "qwen3.6-flash",
				inputPrice: "0.25e-6",
				outputPrice: "1.5e-6",
				cachedInputPrice: "0.05e-6",
				cacheReadInputPrice: "0.025e-6",
				cacheWriteInputPrice: "0.3125e-6",
				pricingTiers: [
					{
						name: "Up to 256K",
						upToTokens: 256000,
						inputPrice: "0.25e-6",
						outputPrice: "1.5e-6",
						cachedInputPrice: "0.05e-6",
						cacheReadInputPrice: "0.025e-6",
						cacheWriteInputPrice: "0.3125e-6",
					},
					{
						name: "Over 256K",
						upToTokens: Infinity,
						inputPrice: "1.0e-6",
						outputPrice: "4e-6",
						cachedInputPrice: "0.2e-6",
						cacheReadInputPrice: "0.1e-6",
						cacheWriteInputPrice: "1.25e-6",
					},
				],
				regions: [
					{ id: "singapore" },
					{
						id: "eu-frankfurt",
						inputPrice: "0.165e-6",
						outputPrice: "0.99e-6",
						cachedInputPrice: "0.033e-6",
						cacheReadInputPrice: "0.0165e-6",
						cacheWriteInputPrice: "0.20625e-6",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.165e-6",
								outputPrice: "0.99e-6",
								cachedInputPrice: "0.033e-6",
								cacheReadInputPrice: "0.0165e-6",
								cacheWriteInputPrice: "0.20625e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "0.66e-6",
								outputPrice: "3.961e-6",
								cachedInputPrice: "0.132e-6",
								cacheReadInputPrice: "0.066e-6",
								cacheWriteInputPrice: "0.825e-6",
							},
						],
					},
					{
						id: "us-virginia",
						inputPrice: "0.165e-6",
						outputPrice: "0.99e-6",
						cachedInputPrice: "0.033e-6",
						cacheReadInputPrice: "0.0165e-6",
						cacheWriteInputPrice: "0.20625e-6",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.165e-6",
								outputPrice: "0.99e-6",
								cachedInputPrice: "0.033e-6",
								cacheReadInputPrice: "0.0165e-6",
								cacheWriteInputPrice: "0.20625e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "0.66e-6",
								outputPrice: "3.961e-6",
								cachedInputPrice: "0.132e-6",
								cacheReadInputPrice: "0.066e-6",
								cacheWriteInputPrice: "0.825e-6",
							},
						],
					},
					{
						id: "cn-beijing",
						inputPrice: "0.165e-6",
						outputPrice: "0.99e-6",
						cachedInputPrice: "0.033e-6",
						cacheReadInputPrice: "0.0165e-6",
						cacheWriteInputPrice: "0.20625e-6",
						pricingTiers: [
							{
								name: "Up to 256K",
								upToTokens: 256000,
								inputPrice: "0.165e-6",
								outputPrice: "0.99e-6",
								cachedInputPrice: "0.033e-6",
								cacheReadInputPrice: "0.0165e-6",
								cacheWriteInputPrice: "0.20625e-6",
							},
							{
								name: "Over 256K",
								upToTokens: Infinity,
								inputPrice: "0.66e-6",
								outputPrice: "3.961e-6",
								cachedInputPrice: "0.132e-6",
								cacheReadInputPrice: "0.066e-6",
								cacheWriteInputPrice: "0.825e-6",
							},
						],
					},
				],
				requestPrice: "0",
				contextSize: 983616,
				maxOutput: 65536,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				reasoningMaxTokens: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				// Qwen thinking models reject tool_choice "required" or object
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
					"reasoning_effort",
				],
			},
		],
	},
	{
		id: "qwen3-embedding-0.6b",
		name: "Qwen3 Embedding 0.6B",
		description:
			"Compact Qwen3 embedding model with 32K context and configurable output dimensions (32–1024) via Matryoshka representation learning, supporting 100+ languages.",
		family: "alibaba",
		output: ["embedding"],
		releasedAt: new Date("2025-06-05"),
		providers: [
			{
				providerId: "deepinfra",
				externalId: "Qwen/Qwen3-Embedding-0.6B",
				inputPrice: "0.01e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 32768,
				streaming: false,
				tools: false,
				jsonOutput: false,
				embeddings: true,
			},
		],
	},
	{
		id: "qwen3-embedding-8b",
		name: "Qwen3 Embedding 8B",
		description:
			"Qwen3 embedding model with 32K context and configurable output dimensions (32–4096) via Matryoshka representation learning. No. 1 on the MTEB multilingual leaderboard as of June 2025 (score 70.58), supporting 100+ languages.",
		family: "alibaba",
		output: ["embedding"],
		releasedAt: new Date("2025-06-05"),
		providers: [
			{
				providerId: "nebius",
				externalId: "Qwen/Qwen3-Embedding-8B",
				deactivatedAt: new Date("2026-08-31"),
				inputPrice: "0.01e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 40960,
				streaming: false,
				tools: false,
				jsonOutput: false,
				embeddings: true,
			},
			{
				providerId: "deepinfra",
				externalId: "Qwen/Qwen3-Embedding-8B",
				inputPrice: "0.01e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 32768,
				streaming: false,
				tools: false,
				jsonOutput: false,
				embeddings: true,
			},
		],
	},
	{
		id: "wan-2-6-t2v",
		name: "Wan 2.6 Text-to-Video",
		description:
			"Alibaba's Wan 2.6 text-to-video model generating short clips with synchronized audio from a text prompt.",
		family: "alibaba",
		output: ["video"],
		maxVideoDurationSeconds: 15,
		releasedAt: new Date("2026-01-15"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "wan2.6-t2v",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				perSecondPrice: {
					default: "0.08",
					"720p": "0.08",
					"1080p": "0.12",
				},
				contextSize: 2000,
				maxOutput: 1,
				streaming: false,
				vision: false,
				tools: false,
				jsonOutput: false,
				videoGenerations: true,
				supportedVideoSizes: ["1280x720", "720x1280", "1920x1080", "1080x1920"],
				supportedVideoDurationsSeconds: [
					2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15,
				],
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: false,
			},
		],
	},
	{
		id: "qwen-audio-3.0-tts-plus",
		name: "Qwen-Audio-3.0-TTS Plus",
		description:
			"Alibaba's high-quality Qwen-Audio-3.0 text-to-speech model with natural prosody and contextually appropriate intonation across 16 languages. Generates speech via the /v1/audio/speech endpoint.",
		family: "alibaba",
		output: ["audio"],
		releasedAt: new Date("2026-07-20"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-audio-3.0-tts-plus",
				inputPrice: "0",
				outputPrice: "0",
				inputCharacterPrice: "20e-6",
				requestPrice: "0",
				contextSize: 20000,
				streaming: false,
				tools: false,
				jsonOutput: false,
				speechGenerations: true,
				supportedVoices: QWEN_AUDIO_TTS_PLUS_VOICES,
			},
		],
	},
	{
		id: "qwen3-reranker-8b",
		name: "Qwen3 Reranker 8B",
		description:
			"Qwen3 cross-encoder reranker with 32K context for search and RAG pipelines. Delivers accurate relevance scoring by jointly encoding query-document pairs. 8B size.",
		family: "alibaba",
		output: ["rerank"],
		releasedAt: new Date("2025-06-06"),
		providers: [
			{
				providerId: "deepinfra",
				externalId: "Qwen/Qwen3-Reranker-8B",
				inputPrice: "0.05e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 32768,
				streaming: false,
				tools: false,
				jsonOutput: false,
				rerank: true,
			},
		],
	},
	{
		id: "qwen-audio-3.0-tts-flash",
		name: "Qwen-Audio-3.0-TTS Flash",
		description:
			"Alibaba's low-latency Qwen-Audio-3.0 text-to-speech model optimized for real-time interaction across 16 languages. Generates speech via the /v1/audio/speech endpoint.",
		family: "alibaba",
		output: ["audio"],
		releasedAt: new Date("2026-07-20"),
		providers: [
			{
				test: "skip",
				providerId: "alibaba",
				externalId: "qwen-audio-3.0-tts-flash",
				inputPrice: "0",
				outputPrice: "0",
				inputCharacterPrice: "15e-6",
				requestPrice: "0",
				contextSize: 20000,
				streaming: false,
				tools: false,
				jsonOutput: false,
				speechGenerations: true,
				supportedVoices: QWEN_AUDIO_TTS_FLASH_VOICES,
			},
		],
	},
	{
		id: "qwen3-reranker-4b",
		name: "Qwen3 Reranker 4B",
		description:
			"Mid-size Qwen3 cross-encoder reranker with 32K context, balancing speed and accuracy for document relevance scoring in search and RAG workloads. 4B size.",
		family: "alibaba",
		output: ["rerank"],
		releasedAt: new Date("2025-06-06"),
		providers: [
			{
				providerId: "deepinfra",
				externalId: "Qwen/Qwen3-Reranker-4B",
				inputPrice: "0.025e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 32768,
				streaming: false,
				tools: false,
				jsonOutput: false,
				rerank: true,
				// DeepInfra scales this deployment to zero: a cold request blocks
				// for ~50s before the first response, which exceeds the e2e timeout.
				// The 0.6B and 8B deployments stay warm and respond in <1s.
				stability: "unstable",
			},
		],
	},
	{
		id: "qwen3-reranker-0.6b",
		name: "Qwen3 Reranker 0.6B",
		description:
			"Lightweight Qwen3 cross-encoder reranker with 32K context for latency-sensitive search and RAG applications. Fast scoring at minimal cost. 0.6B size.",
		family: "alibaba",
		output: ["rerank"],
		releasedAt: new Date("2025-06-06"),
		providers: [
			{
				providerId: "deepinfra",
				externalId: "Qwen/Qwen3-Reranker-0.6B",
				inputPrice: "0.01e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 32768,
				streaming: false,
				tools: false,
				jsonOutput: false,
				rerank: true,
			},
		],
	},
] as const satisfies ModelDefinition[];
